import React, { createContext, useState, useEffect } from "react";
import axios from "axios";

// Create the context with a different name
export const PerformerAWContext = createContext();

// Add 'export' keyword here
export const filterPerformersAW = (performers, filters = {}) => {

  if (!Array.isArray(performers)) return [];
  
  const safeFilters = {
    ethnicity: [],
    age: [],
    fetishes: [],
    build: [],
    searchTerm: '',
    ...filters
  };

  return performers.filter(performer => {
    const hasActiveFilters = Object.values(safeFilters).some(arr => 
      Array.isArray(arr) ? arr.length > 0 : false
    );
    const hasSearchTerm = safeFilters.searchTerm?.trim().length > 0;
    
    if (!hasActiveFilters && !hasSearchTerm) {
      return true;
    }

    // Smart search — same synonym scoring as Streamate performers
    if (hasSearchTerm) {
      const terms = safeFilters.searchTerm.toLowerCase().split(/[\s,]+/).filter(Boolean);
      const SYNONYMS = {
        'latin': 'hispanic', 'latina': 'hispanic', 'latino': 'hispanic',
        'black': 'ebony', 'african': 'ebony',
        'japanese': 'asian', 'korean': 'asian', 'chinese': 'asian',
        'white': 'caucasian', 'arab': 'middleeastern', 'arabic': 'middleeastern',
        'blonde': 'blond', 'brunette': 'brown', 'redhead': 'red', 'ginger': 'red',
        'slim': 'slim', 'skinny': 'slim', 'thick': 'curvy', 'fit': 'athletic',
        'teen': 'teen', 'young': 'teen', 'milf': 'milf', 'mature': 'mature',
        'squirt': 'squirt', 'dom': 'dominat', 'feet': 'feet', 'femdom': 'femdom',
        'lesbian': 'lesbian', 'bdsm': 'bdsm', 'anal': 'anal',
      };
      const name = (performer.displayName || '').toLowerCase();
      const eth  = (performer.ethnicity || '').toLowerCase();
      const hair = (performer.hairColor || '').toLowerCase();
      const age  = parseInt(performer.age) || 0;
      const tags = (performer.details?.willingnesses || []).map(w => w.toLowerCase());
      const allText = [name, eth, hair, ...tags].join(' ');

      const score = terms.reduce((s, t) => {
        const syn = SYNONYMS[t] || t;
        if (name.includes(t)) return s + 10;
        if (eth === t || eth === syn || eth.includes(syn)) return s + 8;
        if (hair === t || hair === syn || hair.includes(t)) return s + 6;
        if ((t === 'teen' || t === 'young') && age >= 18 && age <= 22) return s + 7;
        if ((t === 'milf' || t === 'mature') && age >= 30) return s + 7;
        if (tags.some(tg => tg.includes(syn) || tg.includes(t))) return s + 7;
        if (allText.includes(t)) return s + 3;
        return s;
      }, 0);

      if (score === 0) return false;
    }

    // Filter checks
    const checks = {
      ethnicity: !safeFilters.ethnicity?.length || 
        safeFilters.ethnicity.some(ethnicity => 
          performer.ethnicity?.toLowerCase() === ethnicity.toLowerCase()
        ),

      age: !safeFilters.age?.length || 
        safeFilters.age.some(ageRange => {
          // Get age from persons array
          const age = parseInt(performer.age);
          if (!age) return false;
          
          console.log('Checking age:', { age, ageRange }); // Debug log
          
          switch(ageRange) {
            case "Teen 18+": return age >= 18 && age <= 19;
            case "20s": return age >= 20 && age <= 29;
            case "30s": return age >= 30 && age <= 39;
            case "40s": return age >= 40 && age <= 49;
            case "Mature 50+": return age >= 50;
            default: return false;
          }
        }),

      fetishes: !safeFilters.fetishes?.length || 
        safeFilters.fetishes.some(fetish => 
          performer.details?.willingnesses?.some(w => 
            w?.toLowerCase() === fetish.toLowerCase()
          )
        ),
    };

    return Object.values(checks).every(result => result);
  });
};

// Create the provider component with a different name
export const PerformerAWProvider = ({ children }) => {
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({
    ethnicity: [],
    age: [],
    fetishes: [],
    build: [],
    searchTerm: ''
  });

  // Add handleFilterChange function
  const handleFilterChange = (newFilters) => {
    setSelectedFilters(prev => ({ ...prev, ...newFilters }));
  };

  useEffect(() => {
    const fetchPerformers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/performersAW?limit=100`);
        // Handle error response from API
        if (response.data?.error || !response.data?.data?.models) {
          setPerformers([]);
          setLoading(false);
          return;
        }
        const performersArray = response.data.data.models;
        if (!performersArray || performersArray.length === 0) {
          setPerformers([]);
          setLoading(false);
          return;
        }

        const performersData = performersArray.map((performer) => ({
          uniqueModelId: performer.uniqueModelId,
          performerId: performer.performerId,
          displayName: performer.displayName,
          details: {
            ...performer.details,
            willingnesses: performer.details?.willingnesses || [],
            description: performer.details?.description || ''
          },
          chatRoomUrl: performer.chatRoomUrl,
          profilePictureUrl: (() => {
              const p = performer.profilePictureUrl;
              if (!p) return '';
              if (typeof p === 'string') return p.replace(/^\/\//, 'https://');
              return (p.size160x120 || p.size320x240 || '').replace(/^\/\//, 'https://');
            })(),
          profilePictureFullUrl: (() => {
              const p = performer.profilePictureUrl;
              if (!p) return '';
              if (typeof p === 'string') return p.replace(/^\/\//, 'https://');
              return (p.size320x240 || p.size160x120 || '').replace(/^\/\//, 'https://');
            })(),
          ethnicity: performer.ethnicity || 'N/A',
          age: performer.persons?.[0]?.age || 'N/A',
          category: performer.category || '',
          fetishes: performer.details?.willingnesses || [],
          build: performer.details?.bodyType || performer.category || '',
          livePreRollUrl: performer.livePreRollUrl
        }));

        // Save LJ performers to directory (fire & forget)
        try {
          const batch = performersData.map(p => ({
            name: p.displayName, displayName: p.displayName, network: 'livejasmin',
            ethnicity: p.ethnicity, age: p.age,
            fetishes: Array.isArray(p.fetishes) ? p.fetishes.join(',') : '',
            profilePictureUrl: p.profilePictureUrl,
            chatRoomUrl: p.chatRoomUrl,
          }));
          fetch(`${process.env.REACT_APP_API_URL}/api/performers-directory/upsert`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ performers: batch }),
          }).catch(() => {});
        } catch(e) {}

        setPerformers(performersData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching performers:", error);
        setLoading(false);
      }
    };

    fetchPerformers();
  }, []);

  // Add this effect to filter performers when filters change
  const filteredPerformers = filterPerformersAW(performers, selectedFilters);

  return (
    <PerformerAWContext.Provider value={{ 
      performers: filteredPerformers, // Return filtered performers instead of all performers
      loading, 
      selectedFilters,
      handleFilterChange 
    }}>
      {children}
    </PerformerAWContext.Provider>
  );
};
